import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import {
  useTheme,
  Box,
  Button,
  Stack,
  IconButton,
  Theme,
  Typography,
  Divider,
  Paper,
} from "@mui/material";
import {
  CopyAll,
  ErrorOutline,
  PlayArrow,
  Save,
  Terminal,
} from "@mui/icons-material";
import { MyEditorProps } from "./types";
import type * as monaco from "monaco-editor";
import { userSchema } from "../../schema/userSchema";
import { useBlocker } from "react-router-dom";
import NewAlertModal from "../newAlertModal";

const useStyles = (theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
  } as React.CSSProperties,
  editorContainer: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    border: `1px solid ${theme.palette.stroke}`,
  } as React.CSSProperties,
  logContainer: {
    flexBasis: "200px",
    overflowY: "auto",
    border: `1px solid ${theme.palette.stroke}`,
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
  } as React.CSSProperties,
});

const JsonEditor: React.FC<MyEditorProps> = ({
  json,
  config,
  onApply,
  onSave,
  disabled,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [value, setValue] = useState(
    json ? JSON.stringify(json, null, 2) : "{}"
  );
  const [error, setError] = useState<string | null>(null);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();

  const ajv = useMemo(() => {
    const instance = new Ajv({ allErrors: true, strict: false });
    addFormats(instance);
    instance.compile(userSchema);
    return instance;
  }, []);

  useEffect(() => {
    try {
      const data = JSON.parse(value);
      // const valid = ajv.validate(userSchema, data);
      //setError(valid ? null : ajv.errorsText(ajv.errors!, { separator: "\n" }));
    } catch (e: any) {
      setError(e.message);
    }
  }, [value, ajv]);

  useEffect(() => {
    if (!containerRef.current || !editorRef.current) return;
    const ro = new ResizeObserver(() => editorRef.current!.layout());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleSave, handleApply]);

  useEffect(() => {
    setValue(json ? JSON.stringify(json, null, 2) : "{}");
    //setHasChanges(false);
  }, [json]);

  // const blocker = useBlocker(
  //   ({ currentLocation, nextLocation }) =>
  //     hasChanges && currentLocation.pathname !== nextLocation.pathname
  // );

  // useEffect(() => {
  //   if (blocker.state === "blocked") setOpenAlertModal(true);
  // }, [blocker.state]);

  const handleMount: OnMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: "inmemory://user.schema.json",
          fileMatch: ["*"],
          schema: userSchema,
        },
      ],
    });
    editor.getAction("editor.action.formatDocument")?.run();
    editor.layout();
  };

  function handleSave() {
    try {
      const parsed = JSON.parse(value);
      if (error) return;
      onSave?.(parsed);
      setHasChanges(false);
    } catch {}
  }

  function handleApply() {
    try {
      const parsed = JSON.parse(value);
      if (error) return;
      onApply?.(parsed);
    } catch {}
  }

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === "s") {
        e.preventDefault();
        handleSave();
      } else if (key === "r") {
        e.preventDefault();
        handleApply();
      }
    },
    [handleSave, handleApply]
  );

  const handleRestore = useCallback(() => {
    setValue(json ? JSON.stringify(json, null, 2) : "{}");
    setError(null);
    setHasChanges(false);
  }, [json]);

  return (
    <Box sx={styles.container} key="json-editor-container">
      <Paper ref={containerRef} sx={styles.editorContainer}>
        {!disabled && (
          <Box sx={{ display: "flex", justifyContent: "space-between", px: 1 }}>
            <IconButton onClick={handleSave} disabled={!hasChanges || !!error}>
              <Save sx={{ color: hasChanges ? "primary.main" : "inherit" }} />
            </IconButton>
            <Stack spacing={1} direction="row" sx={{ ml: "auto" }}>
              <Button
                variant="text"
                sx={{ color: hasChanges ? "primary.main" : "inherit" }}
                disabled={!hasChanges || !!error}
                onClick={handleRestore}
              >
                Restaurar
              </Button>
              <Button
                variant="text"
                sx={{ color: "primary.main" }}
                disabled={!hasChanges || !!error}
                onClick={handleApply}
                endIcon={<PlayArrow />}
              >
                Aplicar
              </Button>
            </Stack>
          </Box>
        )}
        <Editor
          key="monaco-editor"
          height={disabled ? "100%" : "calc(100% - 32px)"}
          defaultLanguage="json"
          value={value}
          onMount={handleMount}
          onChange={(v) => {
            setValue(v ?? "");
            setHasChanges(true);
          }}
          theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
          options={{
            minimap: { enabled: false },
            formatOnPaste: true,
            formatOnType: true,
            scrollBeyondLastLine: false,
            automaticLayout: false,
            readOnly: disabled ? true : false,
            ...config?.options,
          }}
          {...config}
        />
      </Paper>

      {!disabled && (
        <Paper sx={styles.logContainer}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Terminal fontSize="small" />
              <Typography sx={{ fontWeight: 600 }}>Log</Typography>
            </Box>
            <IconButton>
              <CopyAll fontSize="small" />
            </IconButton>
          </Box>
          <Divider sx={{ my: 1 }} />
          {error && (
            <Box sx={{ display: "flex" }}>
              <ErrorOutline
                sx={{
                  fontSize: "16px",
                  color: theme.palette.error.main,
                  mt: 0.5,
                  mr: 0.5,
                }}
              />
              <Typography sx={{ fontSize: "14px" }}>{error}</Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default JsonEditor;
